/**
 * Logic Gate SVG Components for LogicSetInfoModal
 * PBI 266671 - Logic Set dynamic palette information
 *
 * These are the Figma-spec versions (68x52) of the logic gates
 * used specifically in the modal diagram.
 */

import React from 'react'
import PropTypes from 'prop-types'

/**
 * Info Icon - Figma spec (20x20)
 * Used in "Learn How..." link and "Select any element..." instruction
 */
export const InfoIcon = ({ color = '#4F5764', opacity = 0.87, size = 20 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: 'block', flexShrink: 0, minWidth: size, minHeight: size }}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M20 10C20 15.5 15.5 20 10 20C4.5 20 0 15.5 0 10C0 4.5 4.5 0 10 0C15.5 0 20 4.5 20 10ZM7.5 13.3705V14.7108H12.5V13.3705H10.9337V7.70783H7.59036V9.04819H9.21687V13.3705H7.5ZM9.24699 6.24699C9.45783 6.44779 9.70884 6.54819 10 6.54819C10.2811 6.54819 10.5271 6.44779 10.738 6.24699C10.9588 6.04618 11.0693 5.80522 11.0693 5.5241C11.0693 5.24297 10.9588 5.00201 10.738 4.8012C10.5271 4.6004 10.2811 4.5 10 4.5C9.68876 4.5 9.43273 4.59538 9.23193 4.78614C9.03112 4.97691 8.93072 5.22289 8.93072 5.5241C8.93072 5.80522 9.03614 6.04618 9.24699 6.24699Z"
      fill={color}
      fillOpacity={opacity}
    />
  </svg>
)

InfoIcon.propTypes = {
  color: PropTypes.string,
  opacity: PropTypes.number,
  size: PropTypes.number,
}

// Color helper for delay icon states - Figma specs
const getDelayIconColor = (selected, hovered) => {
  if (selected) return '#0051C4' // Dark blue - pressed/selected
  if (hovered) return '#1E7AFF' // Blue - hover
  return '#4F5764' // Grey - default
}

/**
 * Activate Delay Icon - Figma spec (30x66)
 * Shows hourglass with "AD" text
 */
export const ActivateDelayIcon = ({ selected = false, hovered = false }) => {
  const color = getDelayIconColor(selected, hovered)
  const opacity = selected || hovered ? 1 : 0.87
  return (
    <svg
      width="30"
      height="66"
      viewBox="0 0 30 66"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M30 18V32.4004H29.9746L30 32.4238L20 42L30 51.5996L29.9746 51.624H30V66H0V51.624H0.0253906L0 51.5996L10 42L0 32.4238L0.0253906 32.4004H0V18H30ZM5 52.7998V61.2002C8.01523 59.9338 10.3485 58.9534 12 58.2598V46.0801L5 52.7998ZM18 58.2598C19.6515 58.9534 21.9848 59.9338 25 61.2002V52.7998L18 46.0801V58.2598ZM5 22.7998V31.2002H25V22.7998H5Z"
        fill={color}
        fillOpacity={opacity}
      />
      <path
        d="M14.9297 15H12.4141L11.4141 12.3984H6.83594L5.89062 15H3.4375L7.89844 3.54688H10.3438L14.9297 15ZM10.6719 10.4688L9.09375 6.21875L7.54688 10.4688H10.6719Z"
        fill={color}
        fillOpacity={opacity}
      />
      <path
        d="M16.1562 3.54688H20.3828C21.3359 3.54688 22.0625 3.61979 22.5625 3.76562C23.2344 3.96354 23.8099 4.3151 24.2891 4.82031C24.7682 5.32552 25.1328 5.94531 25.3828 6.67969C25.6328 7.40885 25.7578 8.3099 25.7578 9.38281C25.7578 10.3255 25.6406 11.138 25.4062 11.8203C25.1198 12.6536 24.7109 13.3281 24.1797 13.8438C23.7786 14.2344 23.237 14.5391 22.5547 14.7578C22.0443 14.9193 21.362 15 20.5078 15H16.1562V3.54688ZM18.4688 5.48438V13.0703H20.1953C20.8411 13.0703 21.3073 13.0339 21.5938 12.9609C21.9688 12.8672 22.2786 12.7083 22.5234 12.4844C22.7734 12.2604 22.9766 11.8932 23.1328 11.3828C23.2891 10.8672 23.3672 10.1667 23.3672 9.28125C23.3672 8.39583 23.2891 7.71615 23.1328 7.24219C22.9766 6.76823 22.7578 6.39844 22.4766 6.13281C22.1953 5.86719 21.8385 5.6875 21.4062 5.59375C21.0833 5.52083 20.4505 5.48438 19.5078 5.48438H18.4688Z"
        fill={color}
        fillOpacity={opacity}
      />
    </svg>
  )
}

ActivateDelayIcon.propTypes = {
  selected: PropTypes.bool,
  hovered: PropTypes.bool,
}

/**
 * Deactivate Delay Icon - Figma spec (30x66)
 * Shows hourglass with "DD" text
 */
export const DeactivateDelayIcon = ({ selected = false, hovered = false }) => {
  const color = getDelayIconColor(selected, hovered)
  const opacity = selected || hovered ? 1 : 0.87
  return (
    <svg
      width="30"
      height="66"
      viewBox="0 0 30 66"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M30 18V32.4004H29.9746L30 32.4238L20 42L30 51.5996L29.9746 51.624H30V66H0V51.624H0.0253906L0 51.5996L10 42L0 32.4238L0.0253906 32.4004H0V18H30ZM5 52.7998V61.2002C8.01523 59.9338 10.3485 58.9534 12 58.2598V46.0801L5 52.7998ZM18 58.2598C19.6515 58.9534 21.9848 59.9338 25 61.2002V52.7998L18 46.0801V58.2598ZM5 22.7998V31.2002H25V22.7998H5Z"
        fill={color}
        fillOpacity={opacity}
      />
      <path
        d="M4.59375 3.54688H8.82031C9.77344 3.54688 10.5 3.61979 11 3.76562C11.6719 3.96354 12.2474 4.3151 12.7266 4.82031C13.2057 5.32552 13.5703 5.94531 13.8203 6.67969C14.0703 7.40885 14.1953 8.3099 14.1953 9.38281C14.1953 10.3255 14.0781 11.138 13.8438 11.8203C13.5573 12.6536 13.1484 13.3281 12.6172 13.8438C12.2161 14.2344 11.6745 14.5391 10.9922 14.7578C10.4818 14.9193 9.79948 15 8.94531 15H4.59375V3.54688ZM6.90625 5.48438V13.0703H8.63281C9.27865 13.0703 9.74479 13.0339 10.0312 12.9609C10.4062 12.8672 10.7161 12.7083 10.9609 12.4844C11.2109 12.2604 11.4141 11.8932 11.5703 11.3828C11.7266 10.8672 11.8047 10.1667 11.8047 9.28125C11.8047 8.39583 11.7266 7.71615 11.5703 7.24219C11.4141 6.76823 11.1953 6.39844 10.9141 6.13281C10.6328 5.86719 10.276 5.6875 9.84375 5.59375C9.52083 5.52083 8.88802 5.48438 7.94531 5.48438H6.90625Z"
        fill={color}
        fillOpacity={opacity}
      />
      <path
        d="M16.1562 3.54688H20.3828C21.3359 3.54688 22.0625 3.61979 22.5625 3.76562C23.2344 3.96354 23.8099 4.3151 24.2891 4.82031C24.7682 5.32552 25.1328 5.94531 25.3828 6.67969C25.6328 7.40885 25.7578 8.3099 25.7578 9.38281C25.7578 10.3255 25.6406 11.138 25.4062 11.8203C25.1198 12.6536 24.7109 13.3281 24.1797 13.8438C23.7786 14.2344 23.237 14.5391 22.5547 14.7578C22.0443 14.9193 21.362 15 20.5078 15H16.1562V3.54688ZM18.4688 5.48438V13.0703H20.1953C20.8411 13.0703 21.3073 13.0339 21.5938 12.9609C21.9688 12.8672 22.2786 12.7083 22.5234 12.4844C22.7734 12.2604 22.9766 11.8932 23.1328 11.3828C23.2891 10.8672 23.3672 10.1667 23.3672 9.28125C23.3672 8.39583 23.2891 7.71615 23.1328 7.24219C22.9766 6.76823 22.7578 6.39844 22.4766 6.13281C22.1953 5.86719 21.8385 5.6875 21.4062 5.59375C21.0833 5.52083 20.4505 5.48438 19.5078 5.48438H18.4688Z"
        fill={color}
        fillOpacity={opacity}
      />
    </svg>
  )
}

DeactivateDelayIcon.propTypes = {
  selected: PropTypes.bool,
  hovered: PropTypes.bool,
}

// Color helper for gate states - Figma specs
const getGateColor = (selected, hovered) => {
  if (selected) return '#0051C4' // Dark blue - pressed/selected
  if (hovered) return '#1E7AFF' // Blue - hover
  return '#687585' // Grey - default
}

// Shared PropTypes for gate components
const gatePropTypes = {
  selected: PropTypes.bool,
  hovered: PropTypes.bool,
}

export const ANDGate = ({ selected = false, hovered = false }) => (
  <svg
    width="68"
    height="52"
    viewBox="0 0 68 52"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M39 0C53.9117 0 66 11.6406 66 26C66 40.3594 53.9117 52 39 52H12V40H0V36H12V16H0V12H12V0H39Z"
      fill={getGateColor(selected, hovered)}
    />
    <path
      d="M31.417 33H28.5869L27.4619 30.0732H22.3115L21.248 33H18.4883L23.5068 20.1152H26.2578L31.417 33ZM26.627 27.9023L24.8516 23.1211L23.1113 27.9023H26.627ZM32.832 33V20.1152H35.3633L40.6367 28.7197V20.1152H43.0537V33H40.4434L35.249 24.5977V33H32.832ZM45.8047 20.1152H50.5596C51.6318 20.1152 52.4492 20.1973 53.0117 20.3613C53.7676 20.584 54.415 20.9795 54.9541 21.5479C55.4932 22.1162 55.9033 22.8135 56.1846 23.6396C56.4658 24.46 56.6064 25.4736 56.6064 26.6807C56.6064 27.7412 56.4746 28.6553 56.2109 29.4229C55.8887 30.3604 55.4287 31.1191 54.8311 31.6992C54.3799 32.1387 53.7705 32.4814 53.0029 32.7275C52.4287 32.9092 51.6611 33 50.7002 33H45.8047V20.1152ZM48.4062 22.2949V30.8291H50.3486C51.0752 30.8291 51.5996 30.7881 51.9219 30.7061C52.3438 30.6006 52.6924 30.4219 52.9678 30.1699C53.249 29.918 53.4775 29.5049 53.6533 28.9307C53.8291 28.3506 53.917 27.5625 53.917 26.5664C53.917 25.5703 53.8291 24.8057 53.6533 24.2725C53.4775 23.7393 53.2314 23.3232 52.915 23.0244C52.5986 22.7256 52.1973 22.5234 51.7109 22.418C51.3477 22.3359 50.6357 22.2949 49.5752 22.2949H48.4062Z"
      fill="white"
    />
  </svg>
)

ANDGate.propTypes = gatePropTypes

export const ORGate = ({ selected = false, hovered = false }) => (
  <svg
    width="68"
    height="52"
    viewBox="0 0 68 52"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M22.8994 0.0078125C42.1748 0.171846 58.9762 10.5844 67.999 26.0039C58.9763 41.4239 42.1752 51.836 22.8994 52V52.0049H6C8.18352 48.2731 9.90925 44.2476 11.1064 40.0039H0V36.0039H12V36.3271C12.6797 32.9909 13.0371 29.5391 13.0371 26.0049C13.0371 22.4703 12.6798 19.0182 12 15.6816V16.0039H0V12.0039H11.1074C9.91025 7.76041 8.18339 3.73541 6 0.00390625H22.8994V0.0078125Z"
      fill={getGateColor(selected, hovered)}
    />
    <path
      d="M24.2734 26.6367C24.2734 25.3242 24.4697 24.2227 24.8623 23.332C25.1553 22.6758 25.5537 22.0869 26.0576 21.5654C26.5674 21.0439 27.124 20.6572 27.7275 20.4053C28.5303 20.0654 29.4561 19.8955 30.5049 19.8955C32.4033 19.8955 33.9209 20.4844 35.0576 21.6621C36.2002 22.8398 36.7715 24.4775 36.7715 26.5752C36.7715 28.6553 36.2061 30.2842 35.0752 31.4619C33.9443 32.6338 32.4326 33.2197 30.54 33.2197C28.624 33.2197 27.1006 32.6367 25.9697 31.4707C24.8389 30.2988 24.2734 28.6875 24.2734 26.6367ZM26.9541 26.5488C26.9541 28.0078 27.291 29.1152 27.9648 29.8711C28.6387 30.6211 29.4941 30.9961 30.5312 30.9961C31.5684 30.9961 32.418 30.624 33.0801 29.8799C33.748 29.1299 34.082 28.0078 34.082 26.5137C34.082 25.0371 33.7568 23.9355 33.1064 23.209C32.4619 22.4824 31.6035 22.1191 30.5312 22.1191C29.459 22.1191 28.5947 22.4883 27.9385 23.2266C27.2822 23.959 26.9541 25.0664 26.9541 26.5488ZM38.8193 33V20.1152H44.2949C45.6719 20.1152 46.6709 20.2324 47.292 20.4668C47.9189 20.6953 48.4199 21.1055 48.7949 21.6973C49.1699 22.2891 49.3574 22.9658 49.3574 23.7275C49.3574 24.6943 49.0732 25.4941 48.5049 26.127C47.9365 26.7539 47.0869 27.1494 45.9561 27.3135C46.5186 27.6416 46.9814 28.002 47.3447 28.3945C47.7139 28.7871 48.209 29.4844 48.8301 30.4863L50.4033 33H47.292L45.4111 30.1963C44.7432 29.1943 44.2861 28.5645 44.04 28.3066C43.7939 28.043 43.5332 27.8643 43.2578 27.7705C42.9824 27.6709 42.5459 27.6211 41.9482 27.6211H41.4209V33H38.8193ZM41.4209 25.5645H43.3457C44.5938 25.5645 45.373 25.5117 45.6836 25.4062C45.9941 25.3008 46.2373 25.1191 46.4131 24.8613C46.5889 24.6035 46.6768 24.2812 46.6768 23.8945C46.6768 23.4609 46.5596 23.1123 46.3252 22.8486C46.0967 22.5791 45.7715 22.4092 45.3496 22.3389C45.1387 22.3096 44.5059 22.2949 43.4512 22.2949H41.4209V25.5645Z"
      fill="white"
    />
  </svg>
)

ORGate.propTypes = gatePropTypes

export const XORGate = ({ selected = false, hovered = false }) => (
  <svg
    width="68"
    height="52"
    viewBox="0 0 68 52"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Extra curved line on the left - distinguishes XOR from OR */}
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6.46973 4.00029C9.6301 10.6821 11.3955 18.138 11.3955 26.0013C11.3955 33.8639 9.63057 41.319 6.4707 48.0003H2C5.44744 41.409 7.39546 33.9293 7.39551 26.0013C7.39547 18.0728 5.44785 10.5919 2 4.00029H6.46973Z"
      fill={getGateColor(selected, hovered)}
    />
    {/* Main OR-shape body */}
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M22.8994 0.0078125C42.1748 0.171846 58.9762 10.5844 67.999 26.0039C58.9763 41.4239 42.1752 51.836 22.8994 52V52.0049H10C12.1835 48.2731 13.9093 44.2476 15.1064 40.0039H0V36.0039H16.0635C16.7011 32.768 17.0371 29.4252 17.0371 26.0049C17.0371 22.5839 16.7013 19.2404 16.0635 16.0039H0V12.0039H15.1074C13.9102 7.76041 12.1834 3.73541 10 0.00390625H22.8994V0.0078125Z"
      fill={getGateColor(selected, hovered)}
    />
    {/* XOR text */}
    <path
      d="M20.4883 33L24.8916 26.2764L20.9014 20.1152H23.9424L26.5264 24.2549L29.0576 20.1152H32.0723L28.0645 26.373L32.4678 33H29.3301L26.4736 28.5439L23.6084 33H20.4883ZM33.2764 26.6367C33.2764 25.3242 33.4727 24.2227 33.8652 23.332C34.1582 22.6758 34.5566 22.0869 35.0605 21.5654C35.5703 21.0439 36.127 20.6572 36.7305 20.4053C37.5332 20.0654 38.459 19.8955 39.5078 19.8955C41.4062 19.8955 42.9238 20.4844 44.0605 21.6621C45.2031 22.8398 45.7744 24.4775 45.7744 26.5752C45.7744 28.6553 45.209 30.2842 44.0781 31.4619C42.9473 32.6338 41.4355 33.2197 39.543 33.2197C37.627 33.2197 36.1035 32.6367 34.9727 31.4707C33.8418 30.2988 33.2764 28.6875 33.2764 26.6367ZM35.957 26.5488C35.957 28.0078 36.2939 29.1152 36.9678 29.8711C37.6416 30.6211 38.4971 30.9961 39.5342 30.9961C40.5713 30.9961 41.4209 30.624 42.083 29.8799C42.751 29.1299 43.085 28.0078 43.085 26.5137C43.085 25.0371 42.7598 23.9355 42.1094 23.209C41.4648 22.4824 40.6064 22.1191 39.5342 22.1191C38.4619 22.1191 37.5977 22.4883 36.9414 23.2266C36.2852 23.959 35.957 25.0664 35.957 26.5488ZM47.8223 33V20.1152H53.2979C54.6748 20.1152 55.6738 20.2324 56.2949 20.4668C56.9219 20.6953 57.4229 21.1055 57.7979 21.6973C58.1729 22.2891 58.3604 22.9658 58.3604 23.7275C58.3604 24.6943 58.0762 25.4941 57.5078 26.127C56.9395 26.7539 56.0898 27.1494 54.959 27.3135C55.5215 27.6416 55.9844 28.002 56.3477 28.3945C56.7168 28.7871 57.2119 29.4844 57.833 30.4863L59.4062 33H56.2949L54.4141 30.1963C53.7461 29.1943 53.2891 28.5645 53.043 28.3066C52.7969 28.043 52.5361 27.8643 52.2607 27.7705C51.9854 27.6709 51.5488 27.6211 50.9512 27.6211H50.4238V33H47.8223ZM50.4238 25.5645H52.3486C53.5967 25.5645 54.376 25.5117 54.6865 25.4062C54.9971 25.3008 55.2402 25.1191 55.416 24.8613C55.5918 24.6035 55.6797 24.2812 55.6797 23.8945C55.6797 23.4609 55.5625 23.1123 55.3281 22.8486C55.0996 22.5791 54.7744 22.4092 54.3525 22.3389C54.1416 22.3096 53.5088 22.2949 52.4541 22.2949H50.4238V25.5645Z"
      fill="white"
    />
  </svg>
)

XORGate.propTypes = gatePropTypes

export const ToggleGate = ({ selected = false, hovered = false }) => (
  <svg
    width="68"
    height="52"
    viewBox="0 0 68 52"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M24.4883 33L28.8916 26.2764L24.9014 20.1152H27.9424L30.5264 24.2549L33.0576 20.1152H36.0723L32.0645 26.373L36.4678 33H33.3301L30.4736 28.5439L27.6084 33H24.4883ZM37.2764 26.6367C37.2764 25.3242 37.4727 24.2227 37.8652 23.332C38.1582 22.6758 38.5566 22.0869 39.0605 21.5654C39.5703 21.0439 40.127 20.6572 40.7305 20.4053C41.5332 20.0654 42.459 19.8955 43.5078 19.8955C45.4062 19.8955 46.9238 20.4844 48.0605 21.6621C49.2031 22.8398 49.7744 24.4775 49.7744 26.5752C49.7744 28.6553 49.209 30.2842 48.0781 31.4619C46.9473 32.6338 45.4355 33.2197 43.543 33.2197C41.627 33.2197 40.1035 32.6367 38.9727 31.4707C37.8418 30.2988 37.2764 28.6875 37.2764 26.6367ZM39.957 26.5488C39.957 28.0078 40.2939 29.1152 40.9678 29.8711C41.6416 30.6211 42.4971 30.9961 43.5342 30.9961C44.5713 30.9961 45.4209 30.624 46.083 29.8799C46.751 29.1299 47.085 28.0078 47.085 26.5137C47.085 25.0371 46.7598 23.9355 46.1094 23.209C45.4648 22.4824 44.6064 22.1191 43.5342 22.1191C42.4619 22.1191 41.5977 22.4883 40.9414 23.2266C40.2852 23.959 39.957 25.0664 39.957 26.5488ZM51.8223 33V20.1152H57.2979C58.6748 20.1152 59.6738 20.2324 60.2949 20.4668C60.9219 20.6953 61.4229 21.1055 61.7979 21.6973C62.1729 22.2891 62.3604 22.9658 62.3604 23.7275C62.3604 24.6943 62.0762 25.4941 61.5078 26.127C60.9395 26.7539 60.0898 27.1494 58.959 27.3135C59.5215 27.6416 59.9844 28.002 60.3477 28.3945C60.7168 28.7871 61.2119 29.4844 61.833 30.4863L63.4062 33H60.2949L58.4141 30.1963C57.7461 29.1943 57.2891 28.5645 57.043 28.3066C56.7969 28.043 56.5361 27.8643 56.2607 27.7705C55.9854 27.6709 55.5488 27.6211 54.9512 27.6211H54.4238V33H51.8223ZM54.4238 25.5645H56.3486C57.5967 25.5645 58.376 25.5117 58.6865 25.4062C58.9971 25.3008 59.2402 25.1191 59.416 24.8613C59.5918 24.6035 59.6797 24.2812 59.6797 23.8945C59.6797 23.4609 59.5625 23.1123 59.3281 22.8486C59.0996 22.5791 58.7744 22.4092 58.3525 22.3389C58.1416 22.3096 57.5088 22.2949 56.4541 22.2949H54.4238V25.5645Z"
      fill="white"
    />
    <rect
      x="16"
      width="52"
      height="52"
      rx="8"
      fill={getGateColor(selected, hovered)}
    />
    <path
      d="M41.708 21V10.2949H37.8848V8.11523H48.124V10.2949H44.3096V21H41.708ZM37.8145 46V33.1152H43.29C44.667 33.1152 45.666 33.2324 46.2871 33.4668C46.9141 33.6953 47.415 34.1055 47.79 34.6973C48.165 35.2891 48.3525 35.9658 48.3525 36.7275C48.3525 37.6943 48.0684 38.4941 47.5 39.127C46.9316 39.7539 46.082 40.1494 44.9512 40.3135C45.5137 40.6416 45.9766 41.002 46.3398 41.3945C46.709 41.7871 47.2041 42.4844 47.8252 43.4863L49.3984 46H46.2871L44.4062 43.1963C43.7383 42.1943 43.2812 41.5645 43.0352 41.3066C42.7891 41.043 42.5283 40.8643 42.2529 40.7705C41.9775 40.6709 41.541 40.6211 40.9434 40.6211H40.416V46H37.8145ZM40.416 38.5645H42.3408C43.5889 38.5645 44.3682 38.5117 44.6787 38.4062C44.9893 38.3008 45.2324 38.1191 45.4082 37.8613C45.584 37.6035 45.6719 37.2812 45.6719 36.8945C45.6719 36.4609 45.5547 36.1123 45.3203 35.8486C45.0918 35.5791 44.7666 35.4092 44.3447 35.3389C44.1338 35.3096 43.501 35.2949 42.4463 35.2949H40.416V38.5645Z"
      fill="white"
    />
    <rect x="30" y="24" width="24" height="2" fill="white" />
    <rect
      y="12.0039"
      width="18"
      height="4"
      fill={getGateColor(selected, hovered)}
    />
    <rect
      y="36.0039"
      width="18"
      height="4"
      fill={getGateColor(selected, hovered)}
    />
  </svg>
)

ToggleGate.propTypes = gatePropTypes

/**
 * Final gate components with output line and arrow icon
 * These are used for the rightmost gate in the diagram
 */

export const ANDGateFinal = ({ selected = false, hovered = false }) => {
  const gateColor = getGateColor(selected, hovered)
  const isActive = selected || hovered
  return (
    <svg
      width="186"
      height="52"
      viewBox="0 0 186 52"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M39 0C53.9117 0 66 11.6406 66 26C66 40.3594 53.9117 52 39 52H12V40H0V36H12V16H0V12H12V0H39Z"
        fill={gateColor}
      />
      <path
        d="M31.417 33H28.5869L27.4619 30.0732H22.3115L21.248 33H18.4883L23.5068 20.1152H26.2578L31.417 33ZM26.627 27.9023L24.8516 23.1211L23.1113 27.9023H26.627ZM32.832 33V20.1152H35.3633L40.6367 28.7197V20.1152H43.0537V33H40.4434L35.249 24.5977V33H32.832ZM45.8047 20.1152H50.5596C51.6318 20.1152 52.4492 20.1973 53.0117 20.3613C53.7676 20.584 54.415 20.9795 54.9541 21.5479C55.4932 22.1162 55.9033 22.8135 56.1846 23.6396C56.4658 24.46 56.6064 25.4736 56.6064 26.6807C56.6064 27.7412 56.4746 28.6553 56.2109 29.4229C55.8887 30.3604 55.4287 31.1191 54.8311 31.6992C54.3799 32.1387 53.7705 32.4814 53.0029 32.7275C52.4287 32.9092 51.6611 33 50.7002 33H45.8047V20.1152ZM48.4062 22.2949V30.8291H50.3486C51.0752 30.8291 51.5996 30.7881 51.9219 30.7061C52.3438 30.6006 52.6924 30.4219 52.9678 30.1699C53.249 29.918 53.4775 29.5049 53.6533 28.9307C53.8291 28.3506 53.917 27.5625 53.917 26.5664C53.917 25.5703 53.8291 24.8057 53.6533 24.2725C53.4775 23.7393 53.2314 23.3232 52.915 23.0244C52.5986 22.7256 52.1973 22.5234 51.7109 22.418C51.3477 22.3359 50.6357 22.2949 49.5752 22.2949H48.4062Z"
        fill="white"
      />
      <path d="M66 26.25L141.5 25.75" stroke={gateColor} strokeWidth="4" />
      <circle
        cx="163.5"
        cy="26"
        r="21"
        fill={isActive ? gateColor : 'white'}
        stroke={isActive ? gateColor : '#8C96A2'}
        strokeWidth="2"
      />
      <path
        d="M179.083 25.9997L167.167 33.3337V27.8337H159.833V24.1667H167.167V18.6667L179.083 25.9997Z"
        fill={isActive ? 'white' : '#0B2E6B'}
      />
      <circle
        cx="152.5"
        cy="26"
        r="3.66667"
        fill={isActive ? 'white' : '#0B2E6B'}
      />
    </svg>
  )
}

ANDGateFinal.propTypes = gatePropTypes

export const ORGateFinal = ({ selected = false, hovered = false }) => {
  const gateColor = getGateColor(selected, hovered)
  const isActive = selected || hovered
  return (
    <svg
      width="185"
      height="52"
      viewBox="0 0 185 52"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M22.8994 0.0078125C42.1748 0.171846 58.9762 10.5844 67.999 26.0039C58.9763 41.4239 42.1752 51.836 22.8994 52V52.0049H6C8.18352 48.2731 9.90925 44.2476 11.1064 40.0039H0V36.0039H12V36.3271C12.6797 32.9909 13.0371 29.5391 13.0371 26.0049C13.0371 22.4703 12.6798 19.0182 12 15.6816V16.0039H0V12.0039H11.1074C9.91025 7.76041 8.18339 3.73541 6 0.00390625H22.8994V0.0078125Z"
        fill={gateColor}
      />
      <path
        d="M24.2734 26.6367C24.2734 25.3242 24.4697 24.2227 24.8623 23.332C25.1553 22.6758 25.5537 22.0869 26.0576 21.5654C26.5674 21.0439 27.124 20.6572 27.7275 20.4053C28.5303 20.0654 29.4561 19.8955 30.5049 19.8955C32.4033 19.8955 33.9209 20.4844 35.0576 21.6621C36.2002 22.8398 36.7715 24.4775 36.7715 26.5752C36.7715 28.6553 36.2061 30.2842 35.0752 31.4619C33.9443 32.6338 32.4326 33.2197 30.54 33.2197C28.624 33.2197 27.1006 32.6367 25.9697 31.4707C24.8389 30.2988 24.2734 28.6875 24.2734 26.6367ZM26.9541 26.5488C26.9541 28.0078 27.291 29.1152 27.9648 29.8711C28.6387 30.6211 29.4941 30.9961 30.5312 30.9961C31.5684 30.9961 32.418 30.624 33.0801 29.8799C33.748 29.1299 34.082 28.0078 34.082 26.5137C34.082 25.0371 33.7568 23.9355 33.1064 23.209C32.4619 22.4824 31.6035 22.1191 30.5312 22.1191C29.459 22.1191 28.5947 22.4883 27.9385 23.2266C27.2822 23.959 26.9541 25.0664 26.9541 26.5488ZM38.8193 33V20.1152H44.2949C45.6719 20.1152 46.6709 20.2324 47.292 20.4668C47.9189 20.6953 48.4199 21.1055 48.7949 21.6973C49.1699 22.2891 49.3574 22.9658 49.3574 23.7275C49.3574 24.6943 49.0732 25.4941 48.5049 26.127C47.9365 26.7539 47.0869 27.1494 45.9561 27.3135C46.5186 27.6416 46.9814 28.002 47.3447 28.3945C47.7139 28.7871 48.209 29.4844 48.8301 30.4863L50.4033 33H47.292L45.4111 30.1963C44.7432 29.1943 44.2861 28.5645 44.04 28.3066C43.7939 28.043 43.5332 27.8643 43.2578 27.7705C42.9824 27.6709 42.5459 27.6211 41.9482 27.6211H41.4209V33H38.8193ZM41.4209 25.5645H43.3457C44.5938 25.5645 45.373 25.5117 45.6836 25.4062C45.9941 25.3008 46.2373 25.1191 46.4131 24.8613C46.5889 24.6035 46.6768 24.2812 46.6768 23.8945C46.6768 23.4609 46.5596 23.1123 46.3252 22.8486C46.0967 22.5791 45.7715 22.4092 45.3496 22.3389C45.1387 22.3096 44.5059 22.2949 43.4512 22.2949H41.4209V25.5645Z"
        fill="white"
      />
      <path d="M65 26.25L140.5 25.75" stroke={gateColor} strokeWidth="4" />
      <circle
        cx="162.5"
        cy="26"
        r="21"
        fill={isActive ? gateColor : 'white'}
        stroke={isActive ? gateColor : '#8C96A2'}
        strokeWidth="2"
      />
      <path
        d="M178.083 25.9997L166.167 33.3337V27.8337H158.833V24.1667H166.167V18.6667L178.083 25.9997Z"
        fill={isActive ? 'white' : '#0B2E6B'}
      />
      <circle
        cx="151.5"
        cy="26"
        r="3.66667"
        fill={isActive ? 'white' : '#0B2E6B'}
      />
    </svg>
  )
}

ORGateFinal.propTypes = gatePropTypes

export const XORGateFinal = ({ selected = false, hovered = false }) => {
  const gateColor = getGateColor(selected, hovered)
  const isActive = selected || hovered
  return (
    <svg
      width="188"
      height="52"
      viewBox="0 0 188 52"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Extra curved line on the left - distinguishes XOR from OR */}
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6.46973 4.00029C9.6301 10.6821 11.3955 18.138 11.3955 26.0013C11.3955 33.8639 9.63057 41.319 6.4707 48.0003H2C5.44744 41.409 7.39546 33.9293 7.39551 26.0013C7.39547 18.0728 5.44785 10.5919 2 4.00029H6.46973Z"
        fill={gateColor}
      />
      {/* Main OR-shape body */}
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M22.8994 0.0078125C42.1748 0.171846 58.9762 10.5844 67.999 26.0039C58.9763 41.4239 42.1752 51.836 22.8994 52V52.0049H10C12.1835 48.2731 13.9093 44.2476 15.1064 40.0039H0V36.0039H16.0635C16.7011 32.768 17.0371 29.4252 17.0371 26.0049C17.0371 22.5839 16.7013 19.2404 16.0635 16.0039H0V12.0039H15.1074C13.9102 7.76041 12.1834 3.73541 10 0.00390625H22.8994V0.0078125Z"
        fill={gateColor}
      />
      {/* XOR text */}
      <path
        d="M20.4883 33L24.8916 26.2764L20.9014 20.1152H23.9424L26.5264 24.2549L29.0576 20.1152H32.0723L28.0645 26.373L32.4678 33H29.3301L26.4736 28.5439L23.6084 33H20.4883ZM33.2764 26.6367C33.2764 25.3242 33.4727 24.2227 33.8652 23.332C34.1582 22.6758 34.5566 22.0869 35.0605 21.5654C35.5703 21.0439 36.127 20.6572 36.7305 20.4053C37.5332 20.0654 38.459 19.8955 39.5078 19.8955C41.4062 19.8955 42.9238 20.4844 44.0605 21.6621C45.2031 22.8398 45.7744 24.4775 45.7744 26.5752C45.7744 28.6553 45.209 30.2842 44.0781 31.4619C42.9473 32.6338 41.4355 33.2197 39.543 33.2197C37.627 33.2197 36.1035 32.6367 34.9727 31.4707C33.8418 30.2988 33.2764 28.6875 33.2764 26.6367ZM35.957 26.5488C35.957 28.0078 36.2939 29.1152 36.9678 29.8711C37.6416 30.6211 38.4971 30.9961 39.5342 30.9961C40.5713 30.9961 41.4209 30.624 42.083 29.8799C42.751 29.1299 43.085 28.0078 43.085 26.5137C43.085 25.0371 42.7598 23.9355 42.1094 23.209C41.4648 22.4824 40.6064 22.1191 39.5342 22.1191C38.4619 22.1191 37.5977 22.4883 36.9414 23.2266C36.2852 23.959 35.957 25.0664 35.957 26.5488ZM47.8223 33V20.1152H53.2979C54.6748 20.1152 55.6738 20.2324 56.2949 20.4668C56.9219 20.6953 57.4229 21.1055 57.7979 21.6973C58.1729 22.2891 58.3604 22.9658 58.3604 23.7275C58.3604 24.6943 58.0762 25.4941 57.5078 26.127C56.9395 26.7539 56.0898 27.1494 54.959 27.3135C55.5215 27.6416 55.9844 28.002 56.3477 28.3945C56.7168 28.7871 57.2119 29.4844 57.833 30.4863L59.4062 33H56.2949L54.4141 30.1963C53.7461 29.1943 53.2891 28.5645 53.043 28.3066C52.7969 28.043 52.5361 27.8643 52.2607 27.7705C51.9854 27.6709 51.5488 27.6211 50.9512 27.6211H50.4238V33H47.8223ZM50.4238 25.5645H52.3486C53.5967 25.5645 54.376 25.5117 54.6865 25.4062C54.9971 25.3008 55.2402 25.1191 55.416 24.8613C55.5918 24.6035 55.6797 24.2812 55.6797 23.8945C55.6797 23.4609 55.5625 23.1123 55.3281 22.8486C55.0996 22.5791 54.7744 22.4092 54.3525 22.3389C54.1416 22.3096 53.5088 22.2949 52.4541 22.2949H50.4238V25.5645Z"
        fill="white"
      />
      <path d="M68 26.25L143.5 25.75" stroke={gateColor} strokeWidth="4" />
      <circle
        cx="165.5"
        cy="26"
        r="21"
        fill={isActive ? gateColor : 'white'}
        stroke={isActive ? gateColor : '#8C96A2'}
        strokeWidth="2"
      />
      <path
        d="M181.083 25.9997L169.167 33.3336V27.8336H161.833V24.1667H169.167V18.6667L181.083 25.9997Z"
        fill={isActive ? 'white' : '#0B2E6B'}
      />
      <circle
        cx="154.5"
        cy="26"
        r="3.66667"
        fill={isActive ? 'white' : '#0B2E6B'}
      />
    </svg>
  )
}

XORGateFinal.propTypes = gatePropTypes

export const ToggleGateFinal = ({ selected = false, hovered = false }) => {
  const gateColor = getGateColor(selected, hovered)
  const isActive = selected || hovered
  return (
    <svg
      width="185"
      height="52"
      viewBox="0 0 185 52"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M24.4883 33L28.8916 26.2764L24.9014 20.1152H27.9424L30.5264 24.2549L33.0576 20.1152H36.0723L32.0645 26.373L36.4678 33H33.3301L30.4736 28.5439L27.6084 33H24.4883ZM37.2764 26.6367C37.2764 25.3242 37.4727 24.2227 37.8652 23.332C38.1582 22.6758 38.5566 22.0869 39.0605 21.5654C39.5703 21.0439 40.127 20.6572 40.7305 20.4053C41.5332 20.0654 42.459 19.8955 43.5078 19.8955C45.4062 19.8955 46.9238 20.4844 48.0605 21.6621C49.2031 22.8398 49.7744 24.4775 49.7744 26.5752C49.7744 28.6553 49.209 30.2842 48.0781 31.4619C46.9473 32.6338 45.4355 33.2197 43.543 33.2197C41.627 33.2197 40.1035 32.6367 38.9727 31.4707C37.8418 30.2988 37.2764 28.6875 37.2764 26.6367ZM39.957 26.5488C39.957 28.0078 40.2939 29.1152 40.9678 29.8711C41.6416 30.6211 42.4971 30.9961 43.5342 30.9961C44.5713 30.9961 45.4209 30.624 46.083 29.8799C46.751 29.1299 47.085 28.0078 47.085 26.5137C47.085 25.0371 46.7598 23.9355 46.1094 23.209C45.4648 22.4824 44.6064 22.1191 43.5342 22.1191C42.4619 22.1191 41.5977 22.4883 40.9414 23.2266C40.2852 23.959 39.957 25.0664 39.957 26.5488ZM51.8223 33V20.1152H57.2979C58.6748 20.1152 59.6738 20.2324 60.2949 20.4668C60.9219 20.6953 61.4229 21.1055 61.7979 21.6973C62.1729 22.2891 62.3604 22.9658 62.3604 23.7275C62.3604 24.6943 62.0762 25.4941 61.5078 26.127C60.9395 26.7539 60.0898 27.1494 58.959 27.3135C59.5215 27.6416 59.9844 28.002 60.3477 28.3945C60.7168 28.7871 61.2119 29.4844 61.833 30.4863L63.4062 33H60.2949L58.4141 30.1963C57.7461 29.1943 57.2891 28.5645 57.043 28.3066C56.7969 28.043 56.5361 27.8643 56.2607 27.7705C55.9854 27.6709 55.5488 27.6211 54.9512 27.6211H54.4238V33H51.8223ZM54.4238 25.5645H56.3486C57.5967 25.5645 58.376 25.5117 58.6865 25.4062C58.9971 25.3008 59.2402 25.1191 59.416 24.8613C59.5918 24.6035 59.6797 24.2812 59.6797 23.8945C59.6797 23.4609 59.5625 23.1123 59.3281 22.8486C59.0996 22.5791 58.7744 22.4092 58.3525 22.3389C58.1416 22.3096 57.5088 22.2949 56.4541 22.2949H54.4238V25.5645Z"
        fill="white"
      />
      <rect x="16" width="52" height="52" rx="8" fill={gateColor} />
      <path
        d="M41.708 21V10.2949H37.8848V8.11523H48.124V10.2949H44.3096V21H41.708ZM37.8145 46V33.1152H43.29C44.667 33.1152 45.666 33.2324 46.2871 33.4668C46.9141 33.6953 47.415 34.1055 47.79 34.6973C48.165 35.2891 48.3525 35.9658 48.3525 36.7275C48.3525 37.6943 48.0684 38.4941 47.5 39.127C46.9316 39.7539 46.082 40.1494 44.9512 40.3135C45.5137 40.6416 45.9766 41.002 46.3398 41.3945C46.709 41.7871 47.2041 42.4844 47.8252 43.4863L49.3984 46H46.2871L44.4062 43.1963C43.7383 42.1943 43.2812 41.5645 43.0352 41.3066C42.7891 41.043 42.5283 40.8643 42.2529 40.7705C41.9775 40.6709 41.541 40.6211 40.9434 40.6211H40.416V46H37.8145ZM40.416 38.5645H42.3408C43.5889 38.5645 44.3682 38.5117 44.6787 38.4062C44.9893 38.3008 45.2324 38.1191 45.4082 37.8613C45.584 37.6035 45.6719 37.2812 45.6719 36.8945C45.6719 36.4609 45.5547 36.1123 45.3203 35.8486C45.0918 35.5791 44.7666 35.4092 44.3447 35.3389C44.1338 35.3096 43.501 35.2949 42.4463 35.2949H40.416V38.5645Z"
        fill="white"
      />
      <rect x="30" y="24" width="24" height="2" fill="white" />
      <rect y="12.0039" width="18" height="4" fill={gateColor} />
      <rect y="36.0039" width="18" height="4" fill={gateColor} />
      <path d="M65 26.25L140.5 25.75" stroke={gateColor} strokeWidth="4" />
      <circle
        cx="162.5"
        cy="26"
        r="21"
        fill={isActive ? gateColor : 'white'}
        stroke={isActive ? gateColor : '#8C96A2'}
        strokeWidth="2"
      />
      <path
        d="M178.083 25.9997L166.167 33.3336V27.8336H158.833V24.1667H166.167V18.6667L178.083 25.9997Z"
        fill={isActive ? 'white' : '#0B2E6B'}
      />
      <circle
        cx="151.5"
        cy="26"
        r="3.66667"
        fill={isActive ? 'white' : '#0B2E6B'}
      />
    </svg>
  )
}

ToggleGateFinal.propTypes = gatePropTypes

/**
 * Get the gate component based on operator type
 */
export const getModalGateComponent = (operator) => {
  const gates = {
    and: ANDGate,
    or: ORGate,
    xor: XORGate,
    toggle: ToggleGate,
  }
  return gates[operator] || ANDGate
}

/**
 * Get the final gate component (with output line and arrow) based on operator type
 */
export const getModalFinalGateComponent = (operator) => {
  const gates = {
    and: ANDGateFinal,
    or: ORGateFinal,
    xor: XORGateFinal,
    toggle: ToggleGateFinal,
  }
  return gates[operator] || ANDGateFinal
}

export default {
  InfoIcon,
  ANDGate,
  ORGate,
  XORGate,
  ToggleGate,
  ANDGateFinal,
  ORGateFinal,
  XORGateFinal,
  ToggleGateFinal,
  getModalGateComponent,
  getModalFinalGateComponent,
}
